import { Option, Select } from '@d-edge/overnight-hotelier-react'
import { useMemo, useState } from 'react'
import { useFailedJobs, useJobMutations, useJobs } from '@/api/queries'
import type { FailedJob, ScheduledJob } from '@/api/types'
import { DataTable, type Column } from '@/components/DataTable/DataTable'
import { Badge } from '@/components/ui/Badge'
import { Button, IconButton } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Icons } from '@/components/ui/Icons'
import { Modal } from '@/components/ui/Modal'
import { Tabs } from '@/components/ui/Tabs'
import { useToast } from '@/components/ui/Toast'
import { dateTime } from '@/lib/format'
import { JobForm } from './JobForm'
import { JobHistoryDialog } from './JobHistoryDialog'
import styles from './scheduler.module.css'

type Tab = 'jobs' | 'failures'
type Pending = { job: ScheduledJob; action: 'run' | 'delete' }

interface ScheduledJobsDialogProps {
  initialScheduler?: string
  onClose: () => void
}

export function ScheduledJobsDialog({ initialScheduler, onClose }: ScheduledJobsDialogProps) {
  const { data: jobs, isLoading, error } = useJobs(true)
  const { data: failed, isLoading: failedLoading, error: failedError } = useFailedJobs()
  const { remove, run } = useJobMutations()
  const toast = useToast()
  const [tab, setTab] = useState<Tab>(initialScheduler ? 'failures' : 'jobs')
  const [scheduler, setScheduler] = useState(initialScheduler ?? '')
  const [editing, setEditing] = useState<ScheduledJob | 'new' | null>(null)
  const [history, setHistory] = useState<ScheduledJob | null>(null)
  const [confirm, setConfirm] = useState<Pending | null>(null)

  const schedulers = useMemo(() => [...new Set([...(jobs ?? []), ...(failed ?? [])].map((j) => j.schedulerName))].sort(), [jobs, failed])
  const visibleJobs = useMemo(() => (scheduler ? jobs?.filter((j) => j.schedulerName === scheduler) : jobs), [jobs, scheduler])
  const visibleFailed = useMemo(() => (scheduler ? failed?.filter((f) => f.schedulerName === scheduler) : failed), [failed, scheduler])

  const execute = async ({ job, action }: Pending) => {
    setConfirm(null)
    try {
      if (action === 'delete') {
        await remove.mutateAsync(job.id)
        toast.notify(`${job.name} deleted`, 'success')
      } else {
        const result = await run.mutateAsync(job.id)
        toast.notify(`${job.name}: HTTP ${result.statusCode}${result.body ? `\n${result.body.slice(0, 300)}` : ''}`, result.success ? 'success' : 'error')
      }
    } catch (err) {
      toast.error(err, action === 'delete' ? 'Delete failed' : 'Run failed')
    }
  }

  const actionButtons = (job: ScheduledJob, full: boolean) => (
    <span className="u-row">
      <IconButton label="Run now" onClick={() => setConfirm({ job, action: 'run' })}>
        <Icons.Play size={14} />
      </IconButton>
      {full && (
        <IconButton label="Edit" onClick={() => setEditing(job)}>
          <Icons.Edit size={14} />
        </IconButton>
      )}
      <IconButton label="History" onClick={() => setHistory(job)}>
        <Icons.History size={14} />
      </IconButton>
      {full && (
        <IconButton label="Delete" onClick={() => setConfirm({ job, action: 'delete' })}>
          <Icons.Trash size={14} />
        </IconButton>
      )}
    </span>
  )

  const jobColumns: Column<ScheduledJob>[] = [
    { key: 'enabled', header: '', width: '60px', render: (j) => <Badge tone={j.enabled ? 'ok' : 'neutral'}>{j.enabled ? 'on' : 'off'}</Badge> },
    { key: 'schedulerName', header: 'Scheduler' },
    { key: 'name', header: 'Job', render: (j) => <strong title={j.description ?? undefined}>{j.name}</strong> },
    {
      key: 'frequency',
      header: 'Schedule',
      value: (j) => `${j.interval} ${j.frequency}`,
      render: (j) => (
        <span title={`From ${dateTime(j.start)}${j.end ? ` to ${dateTime(j.end)}` : ''}`}>
          every {j.interval} {j.frequency.toLowerCase()}
        </span>
      ),
    },
    {
      key: 'url',
      header: 'Request',
      render: (j) => (
        <span className={styles.url} title={j.url}>
          <Badge>{j.httpMethod}</Badge>
          {j.url}
        </span>
      ),
    },
    {
      key: 'authorization',
      header: 'Auth',
      render: (j) => (
        <span className="u-row">
          {j.authorization}
          {j.hasCredentials && <Icons.Check size={12} aria-label="Credentials stored" />}
          {j.hasHeaders && (
            <span className="u-faint" title="Custom headers stored">
              H
            </span>
          )}
        </span>
      ),
    },
    { key: 'actions', header: '', sortable: false, width: '132px', render: (j) => actionButtons(j, true) },
  ]

  const failedColumns: Column<FailedJob>[] = [
    { key: 'failedAt', header: 'Failed at', width: '150px', render: (f) => dateTime(f.failedAt) },
    { key: 'schedulerName', header: 'Scheduler' },
    { key: 'name', header: 'Job', render: (f) => <strong>{f.name}</strong> },
    {
      key: 'message',
      header: 'Message',
      render: (f) => (
        <span className={styles.message} title={f.message ?? undefined}>
          {f.message ?? '–'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      sortable: false,
      width: '70px',
      render: (f) => {
        const job = jobs?.find((j) => j.id === f.id)
        return job ? actionButtons(job, false) : null
      },
    },
  ]

  const tabs: { id: Tab; label: string }[] = [
    { id: 'jobs', label: `Jobs${visibleJobs ? ` (${visibleJobs.length})` : ''}` },
    { id: 'failures', label: `Failures · 24h${visibleFailed ? ` (${visibleFailed.length})` : ''}` },
  ]

  const schedulerFilter = (
    <Select value={scheduler} onChange={setScheduler} aria-label="Scheduler" className={styles.select}>
      <Option value="" label="All schedulers" />
      {schedulers.map((s) => (
        <Option key={s} value={s} label={s} />
      ))}
    </Select>
  )

  return (
    <Modal
      title="Scheduled jobs"
      onClose={onClose}
      size="xl"
      headerExtra={
        <Button size="sm" variant="primary" icon={<Icons.Plus size={12} />} onClick={() => setEditing('new')}>
          New job
        </Button>
      }
    >
      <Tabs tabs={tabs} value={tab} onChange={setTab} />
      <div style={{ marginTop: 12 }}>
        {tab === 'jobs' ? (
          <DataTable
            columns={jobColumns}
            rows={visibleJobs}
            rowKey={(j) => j.id}
            loading={isLoading}
            error={error}
            searchable
            searchPlaceholder="Search a job, scheduler or URL"
            toolbar={schedulerFilter}
            dense
            initialSort={{ key: 'name', direction: 'asc' }}
            emptyMessage="No scheduled job"
          />
        ) : (
          <DataTable
            columns={failedColumns}
            rows={visibleFailed}
            rowKey={(f) => `${f.id}:${f.failedAt}`}
            loading={failedLoading}
            error={failedError}
            searchable
            searchPlaceholder="Search a failed job or message"
            toolbar={schedulerFilter}
            dense
            initialSort={{ key: 'failedAt', direction: 'desc' }}
            emptyMessage="No failure in the last 24 hours"
            expandable={(f) => <pre className={styles.details}>{f.message ?? 'No message recorded'}</pre>}
          />
        )}
      </div>
      {editing && <JobForm job={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
      {history && <JobHistoryDialog job={history} onClose={() => setHistory(null)} />}
      {confirm && (
        <ConfirmDialog
          title={confirm.action === 'run' ? `Run ${confirm.job.name} now` : `Delete ${confirm.job.name}`}
          message={
            confirm.action === 'run'
              ? `Call ${confirm.job.httpMethod} ${confirm.job.url} right away? The result is recorded in the job history.`
              : 'The job and its history are removed. This cannot be undone.'
          }
          confirmLabel={confirm.action === 'run' ? 'Run' : 'Delete'}
          danger={confirm.action === 'delete'}
          busy={run.isPending || remove.isPending}
          onCancel={() => setConfirm(null)}
          onConfirm={() => void execute(confirm)}
        />
      )}
    </Modal>
  )
}
